import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import loadSDK from "core/utils/loadSDK";
import apm from "core/utils/apm";
import Grid from "@mui/material/Grid";
import formatCurrency from "core/utils/currency";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import LinearProgress from "@mui/material/LinearProgress";

import { useTranslation } from "react-i18next";

/* global Robokassa */
const SRC = "https://auth.robokassa.ru/Merchant/bundle/robokassa_iframe.js";
const ID = "robokassawidget";

/**
 * Компонент реализует подгрузку SDK робокассы и механизм создания платежа с помощью открытия iframe
 *
 * @param {Object} paymentData - Данные для создания платежа
 * @param {String} returnUrl - Адрес страницы на которую необходимо редиректнуть после закрытия айфрейма с формой платежа
 * @param {Object} amount - Сумма с валютой
 * @param {Object} descriptionComponent - Компонент описания подписки
 */
export default function RobokassaProcessor({
  descriptionComponent,
  paymentData,
  returnUrl,
  amount,
}) {
  const [loading, setLoading] = useState(true);

  const { t } = useTranslation("arena");

  const amountText = formatCurrency(amount.value, amount.currency);

  useEffect(() => {
    if (!process.browser) return;
    if (!paymentData) return;

    loadSDK(SRC, ID)
      .then(() => setLoading(false))
      .catch((error) => {
        apm.captureError(error);
        console.log(error);
      });
  }, [!!paymentData]);

  const onClick = () => {
    setLoading(true);

    // Актуальный robokassa_iframe.js сам закрывает форму по paymentSuccess /
    // paymentFail и сообщает результат вторым аргументом StartPayment через
    // onComplete({ status: "success" | "fail" | "close" }). Сообщение
    // closeRobokassaFrame в window приходит только при закрытии формы вручную,
    // поэтому слушать window "message", как раньше, нельзя: после оплаты
    // редирект на returnUrl не происходил и диалог с платежом не открывался.
    // Редиректим при любом исходе, как и раньше: на returnUrl по paymentId
    // откроется диалог с актуальным статусом платежа.
    Robokassa.StartPayment(paymentData, {
      onComplete: () => {
        window.location = returnUrl;
      },
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Fade in={loading}>
          <LinearProgress />
        </Fade>

        {descriptionComponent && (
          <Grid item xs={12}>
            {descriptionComponent}
          </Grid>
        )}

        <Grid item xs={12}>
          <Button
            disableElevation
            fullWidth
            variant="contained"
            color="primary"
            onClick={onClick}
          >
            {t("stripePaymentForm.pay", { amount: amountText })}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

RobokassaProcessor.propTypes = {
  descriptionComponent: PropTypes.object,
  paymentData: PropTypes.object,
  returnUrl: PropTypes.string,
  amount: PropTypes.object.isRequired,
};
